import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { EnumRunControlState } from '../../enums/run-control-state.enum';
import { EnumRunPhase } from '../../enums/run-phase.enum';
import { IActivity } from '../../interfaces/activity.interface';
import { activityHeadline } from '../../utils/activity-headline.util';
import { IChatInputMessage } from '../../interfaces/chat-input.interface';
import { IChatMessage } from '../../interfaces/chat-message.interface';
import { IConversationFile } from '../../interfaces/conversation-file.interface';
import { IMessageDayGroup } from '../../interfaces/message-day-group.interface';
import { IHITLDecision, IHITLRequest } from '../../interfaces/hitl.interface';
import { AyaService } from '../../services/aya.service';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { groupMessagesByDay } from '../../utils/day-separator.util';
import { collectConversationFiles } from '../../utils/collect-conversation-files.util';

@Component({
  selector: 'sgt-chat-conversation',
  templateUrl: './chat-conversation.component.html',
  styleUrls: ['./chat-conversation.component.scss']
})
export class ChatConversationComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private readonly sessionId$ = new BehaviorSubject<string | null>(null);

  @Input() set sessionId(value: string | null) {
    this.sessionId$.next(value);
  }
  get sessionId(): string | null {
    return this.sessionId$.value;
  }

  @Input() loadingHistory = false;
  @Input() inputPlaceholder = 'Pergunte alguma coisa ou peça uma ação...';
  @Input() variant: 'page' | 'panel' = 'page';

  @Output() messageSent = new EventEmitter<IChatInputMessage>();

  @ViewChild('messagesContainerRef') messagesContainerRef!: ElementRef;
  @ViewChild('chatInput') chatInput!: ChatInputComponent;

  messages: IChatMessage[] = [];
  conversationFiles: IConversationFile[] = [];
  messageGroups: IMessageDayGroup[] = [];
  loading = false;
  activities: IActivity[] = [];
  phase: EnumRunPhase | null = null;
  activityHeadline: string | null = null;
  runControl: EnumRunControlState = EnumRunControlState.IDLE;
  interrupt: IHITLRequest | null = null;

  @HostBinding('class.conversation--panel') get isPanel(): boolean {
    return this.variant === 'panel';
  }

  @HostBinding('class.conversation--page') get isPage(): boolean {
    return this.variant === 'page';
  }

  private destroy$ = new Subject<void>();

  constructor(private ayaService: AyaService) {}

  ngOnInit(): void {
    this.subscribeToMessages();
    this.subscribeToTypingState();
    this.subscribeToActivities();
    this.subscribeToRunControl();
    this.subscribeToInterrupt();
  }

  ngAfterViewInit(): void {
    this.focus();
  }

  focus(): void {
    setTimeout(() => this.chatInput?.focus(), 0);
  }

  onMessageSent(msg: IChatInputMessage): void {
    this.messageSent.emit(msg);
  }

  onCopyMessage(message: IChatMessage): void {
    this.ayaService.copyToClipboard(message);
  }

  onRespond(decisions: IHITLDecision[]): void {
    if (this.sessionId) {
      this.ayaService.respondToInterrupt(this.sessionId, decisions);
    }
  }

  get lastMessage(): IChatMessage | undefined {
    return this.messages[this.messages.length - 1];
  }

  trackByMessageId(_: number, message: IChatMessage): string {
    return message.localId;
  }

  trackByGroupLabel(_: number, group: IMessageDayGroup): string {
    return group.label;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStopRequested(): void {
    if (this.sessionId) {
      this.ayaService.stopRun(this.sessionId);
    }
  }

  private subscribeToMessages(): void {
    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getMessages$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((messages) => {
        this.messages = messages;
        this.conversationFiles = collectConversationFiles(messages);
        this.messageGroups = groupMessagesByDay(messages);
        this.scrollToBottom();
      });
  }

  private subscribeToActivities(): void {
    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getActivities$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((activities) => {
        this.activities = activities;
        this.refreshActivityHeadline();
      });

    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getPhase$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((phase) => {
        this.phase = phase;
        this.refreshActivityHeadline();
      });
  }

  private subscribeToRunControl(): void {
    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getRunControl$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((state) => (this.runControl = state));
  }

  private refreshActivityHeadline(): void {
    this.activityHeadline = activityHeadline(this.activities, this.phase);
  }

  private subscribeToTypingState(): void {
    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getIsTyping$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((isTyping) => {
        const wasTyping = this.loading;
        this.loading = isTyping;

        if (isTyping) {
          this.scrollToBottom();
        }

        if (wasTyping && !isTyping) {
          this.scrollToBottom();
          setTimeout(() => this.chatInput?.focus(), 100);
        }
      });
  }

  private subscribeToInterrupt(): void {
    this.sessionId$
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => this.ayaService.getInterrupt$(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((interrupt) => {
        this.interrupt = interrupt;
        if (interrupt) {
          this.scrollToBottom();
        }
      });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainerRef) {
        const el = this.messagesContainerRef.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
